import { useCallback, useMemo, useState } from 'react'

import { getFractionMixedToImproperQuestionData, FractionModel } from './FractionMixedToImproper.utils'

interface UseFractionImproperToMixedReturnType {
  question?: FractionModel
  formValid: boolean
  inputs?: InputModel
  error?: ErrorModel
  handleCheckAnswer: () => void
  onInputChange: (input?: InputTypes, val?: string) => void
  goToNextQuestion: () => void
}

interface UseProperFractionProps {
  difficultyLevel: number
  onNextQuestionClick: () => void
  onAnswerStateChange: (value: boolean | null) => void
}

interface InputModel {
  numerator?: string
  denominator?: string
}

interface ErrorModel {
  numerator?: boolean
  denominator?: boolean
  quotient?: boolean
}

type InputTypes = 'numerator' | 'denominator' | 'quotient'

function useFractionMixedToImproper({
  difficultyLevel,
  onAnswerStateChange,
  onNextQuestionClick,
}: UseProperFractionProps): UseFractionImproperToMixedReturnType {
  // eslint-disable-next-line
  const [taskHistory, setTaskHistory] = useState<number[]>([])
  const [error, setError] = useState<ErrorModel>()
  const { question, answer, maxHistory } = useMemo(
    () => getFractionMixedToImproperQuestionData(difficultyLevel, taskHistory),
    [difficultyLevel, taskHistory],
  )

  const [inputs, setInputs] = useState<InputModel>({})

  const handleCheckAnswer = useCallback((): void => {
    const { numerator = '0', denominator = '0' } = inputs
    const err: ErrorModel = {
      numerator: parseInt(numerator) !== answer.numerator,
      denominator: parseInt(denominator) !== answer.denominator,
    }
    onAnswerStateChange(!(err.numerator || err.denominator))
    setError(err)
  }, [onAnswerStateChange, inputs, answer])

  const onInputChange = useCallback(
    (input?: InputTypes, val?: string) => {
      switch (input) {
        case 'numerator':
          setInputs({ ...inputs, numerator: val })
          break
        case 'denominator':
          setInputs({ ...inputs, denominator: val })
          break
        default:
          setInputs({})
      }
    },
    [inputs],
  )

  const goToNextQuestion = useCallback(() => {
    setTaskHistory((prevState) => [question.numerator, ...prevState].slice(0, maxHistory))
    onInputChange()
    onNextQuestionClick()
  }, [onInputChange, onNextQuestionClick, maxHistory, question])

  return {
    question,
    formValid: !!inputs.numerator && !!inputs.denominator,
    inputs,
    error,
    handleCheckAnswer,
    onInputChange,
    goToNextQuestion,
  }
}

export default useFractionMixedToImproper
